/* eslint-disable @typescript-eslint/no-unused-vars */
// External libraries
import React, { useState , useRef } from 'react';
import { useLocation } from 'react-router-dom-v5-compat';
import { observer, inject } from 'mobx-react';
import { useDialog } from '@shared-ui/dialog-context';
import { useLocalization } from '@shared-ui/localization-context';
import { useBexApi } from '@shared-ui/bex-api-context';
import { Viewport, ViewSmall, ViewMedium } from '@shared-ui/viewport-context';

// Uitk components
import { UitkCard, UitkCardLink, UitkCardContentSection, UitkCardPlaceholder } from 'uitk-react-cards';
import { UitkSpacing } from 'uitk-react-spacing';
import { UitkLayoutGrid } from 'uitk-react-layout-grid';
import { UitkHeading, UitkText } from 'uitk-react-text';
import { UitkButtonSize, UitkPrimaryButton } from 'uitk-react-button';
import { UitkLayoutFlex, UitkLayoutFlexItem } from 'uitk-react-layout-flex';
import { UitkFigure, UitkFigureAspectRatioType } from 'uitk-react-images';
import { UitkIcon, UitkIconSize } from 'uitk-react-icons';
import { UitkRating } from 'uitk-react-rating';
import { UitkLayoutPosition } from 'uitk-react-layout-position';
import { UitkPriceLockup } from 'uitk-react-price-lockup';

import { css } from 'styled-components';

import { useCampaignById, CampaignByIdResponse } from 'src/components/common/modules/expedia-deals/hotel/hooks/useCampaignById'; 
import { CampaignByIdQuery, CampaignByIdQueryQueryVariables } from 'src/common/__generated__/api/types';
import HotelOfferItemInlineFragment = CampaignByIdQuery.HotelOfferItemInlineFragment;
import { LazyLoadedImage } from 'src/components/common/modules/lazy/lazy-loaded-image';
import {
  DealsProps,
  DealsCarouselViewsProps,
  DealsComponentProps,
  DealsViewsProps,
  PriceComponentProps,
} from './types';
import { mockResponse } from 'src/components/common/modules/expedia-deals/hotel/hotel-data/mock/mock-response-campaginid';
import { ImageBadges } from 'src/components/common/modules/expedia-deals/hotel/hotel-view/components/support/deal-image-badges';
import { Separator } from 'src/components/common/modules/expedia-deals/hotel/hotel-view/components/support/deal-separator';
import { DealsLoader } from 'src/components/common/modules/expedia-deals/hotel/hotel-view/components/support/deal-loader';
import { TrackedLink } from 'src/components/common/modules/analytics/tracked-link';
import { Action } from 'src/components/common/modules/analytics/analytics-utils';
import { InfoDialog } from 'src/components/common/modules/info-dialog/info-dialog';
import { buildFetchMoreDeals, isGridSlickView } from './deals-utils';
import { checkValue, mcicidFormatText } from 'src/components/common/helper';
import { Sticker } from 'src/components/common/ui';

import Layout from 'src/components/common/theme/Layout';
import { gridSlickView } from 'src/components/common/modules/expedia-deals/hotel/hotel-view/views/grid-slick-view';
import { DealLoadingCards } from 'src/components/common/modules/expedia-deals/hotel/hotel-view/components/support/deal-loading-cards';
import { DealLoadingCardsProps } from 'src/components/common/modules/expedia-deals/hotel/hotel-view/hotel-module-types';
import gaLogger from 'src/utils/ga-logger';
import { getDeviceImage } from 'src/components/common/theme/component';
import { StickerProps } from 'src/components/common/module-util';

const maxPropertiesCount = 48;
let dealCampaignId = '';
let initialPropertiesCount = 6;
let propertiesCount = initialPropertiesCount;
let tracking = 'cp.cplite.sports';

const HotelByCampaignId: React.FC<DealsProps> = (dealsProps: DealsProps) => {
  const {
    campaignId,
    destinationId,
    pageNumber = 1,
    pageSize = 6,
    viewMoreCtaText,
    title,
    subheadline,
    summary,
    propertyCount,
    mcicidTemplateType,
    mcicidCampaignUrl,
    mcicidReferrerId,
    mcicidMainUrl,
    extraParam="",
    showCampaignTitles,
    enableTripAttach,
    displayPropertyCount,
    enableLivePricing,
    visibleControl,
    stickerImage,
    stickerPosition,
    isPagination,
    parentId,
    cTAUrl
  } = dealsProps;
  dealCampaignId = campaignId;
  const location = useLocation();
  const { context } = useBexApi();
  tracking = mcicidFormatText(mcicidTemplateType, mcicidCampaignUrl,mcicidMainUrl, mcicidReferrerId , extraParam);
  initialPropertiesCount = propertyCount;
  propertiesCount = propertyCount;

  const queryParams = new URLSearchParams(location.search);

  const campaignByIdVariables = {
    context,
    campaignId,
    enableTripAttach: checkValue(enableTripAttach) ? enableTripAttach : false,
    pageNumber,
    userLocation: {},
    displayPropertyCount: checkValue(displayPropertyCount) ? displayPropertyCount : true,
    referrer: queryParams.get('referrer') || undefined,
    enableLivePricing: checkValue(enableLivePricing) ? enableLivePricing : false,
  };

  if (checkValue(destinationId)) {
    campaignByIdVariables['destinationId'] = destinationId;
  }
 
  const { data, error, loading, fetchMore } = useCampaignById(campaignByIdVariables);

  let isLoadingHtml = <></>;
  let dealPropertyHtml = <></>;

  if (loading) {

    const dealLoadingCardsProps: DealLoadingCardsProps = {
      view: { view: 'grid' },
      desktopLoadingCards: checkValue(propertyCount) ? propertyCount : 3,
    };

    isLoadingHtml = (
      <>
        {DealLoadingCards(dealLoadingCardsProps)}
      </>
    );
  } else {
    /* Overide paginationNumber erroroffersfield without lose all the others values */
    const paginationFetch = buildFetchMoreDeals<CampaignByIdQueryQueryVariables, CampaignByIdResponse>(
      fetchMore,
      campaignByIdVariables
    );

    const stickerProps: StickerProps = {
      stickerImage,
      stickerPosition,
      exClass: 'hotel-sticker-container'
    };

    dealPropertyHtml = (
      <>
        <div className={`hotel-search-results-container ${!checkValue(cTAUrl) ? 'bottom-extra-space-mobile' : ''}`}>
        { !loading && <Sticker {...stickerProps} /> }
        <DealsComponent
          data={data}
          fetchMore={paginationFetch}
          errorMessage={error?.message}
          viewMoreCtaText={viewMoreCtaText}
          visibleControl={visibleControl}
          isPagination={isPagination}
        />
        </div>
      </>
    );
  }

  // eslint-disable-next-line @typescript-eslint/no-empty-function
  const moduleCss = () => {/*  */};
  const isTestDomain = window?.location?.href?.indexOf('sandbox') > -1;
  
  if (
    !isTestDomain &&
    ((!loading && !checkValue(data)) ||
      (!loading &&
        checkValue(data) &&
        (!checkValue(data?.campaignRecommendations) || !checkValue(data?.campaignRecommendations?.campaigns, true))))
  ) {
    const elm = document.getElementById(parentId)
    if(elm) {
      elm.style.display = 'none'
    }
    return <></>;
  }

  const activeShowCampaignTitles = checkValue(showCampaignTitles) ? showCampaignTitles : false;
  const dealCampaign = () => {
    return <>{loading ? <>{isLoadingHtml}</> : <>{dealPropertyHtml}</>}</>;
  };

  return (
    <>
       {activeShowCampaignTitles ? (
        <>{dealCampaign()}</>
       ) : (
        null
       )}      
    </>
  );
};

const DealsComponent: React.FC<DealsComponentProps> = inject('context')(
  observer((props) => {
    const { data, viewMoreCtaText, visibleControl , isPagination } = props;
    const showMoreButtonRef: React.RefObject<HTMLButtonElement> = React.createRef();
    const [isLoadingNextPage, setIsLoadingNextPage] = React.useState<boolean>(false);
    const campaign = data?.campaignRecommendations?.campaigns?.[0];

    React.useEffect(() => {
      showMoreButtonRef.current?.blur();
    }, []);

    React.useEffect(() => {
      setIsLoadingNextPage(false);
    }, [data]);
  
    const isTestDomain = window?.location?.href?.indexOf('sandbox') > -1;
    const loadCampaign = isTestDomain ? mockResponse : campaign;
    const loadCampaignOffers = loadCampaign?.offers;    
    const [propertyCount, setPropertyCount] = useState(propertiesCount);
    const isHasMoreProperties =
      checkValue(loadCampaignOffers) &&
      loadCampaignOffers?.length > 0 &&
      loadCampaignOffers?.length > initialPropertiesCount &&
      loadCampaignOffers?.length > propertyCount &&
      Number(maxPropertiesCount) > Number(propertiesCount);
      const [hasMoreProperties, setHasMoreProperties] = useState(isHasMoreProperties);

    const loadMoreDeals = (): void => {
      showMoreButtonRef.current?.blur();
      setIsLoadingNextPage(true);
      propertiesCount = Number(propertyCount) + Number(initialPropertiesCount);
      setPropertyCount(propertiesCount);
      setHasMoreProperties(Number(maxPropertiesCount) > Number(propertiesCount));
      setIsLoadingNextPage(false);
      gaLogger.logGa('Hotel widget', 'View more button click', 'View More Hotels');
    };
   
    const dealsViewsProps: DealsViewsProps = {
      campaign: loadCampaignOffers?.slice(0, propertyCount),
      visibleControl: visibleControl,
      isPagination:isPagination
    };

    const showMoreTrack = (moduleName: string) => {/* */};

    const getVisibleControl = isGridSlickView(visibleControl);
    return (
      <>
        <DealsView {...dealsViewsProps} />
 
        {getVisibleControl && hasMoreProperties && checkValue(viewMoreCtaText) && (
          <Viewport>
          <ViewMedium>
          <div className='hotel-widget-action-container'>
            <UitkLayoutFlex justifyContent="center">
              <UitkLayoutFlexItem>
                <UitkPrimaryButton
                  className={'show-more'}
                  name="showMoreButton"
                  type="button"
                  size={UitkButtonSize.LARGE}
                  onClick={loadMoreDeals}
                  domRef={showMoreButtonRef}
                  analytics={{
                    id: `${tracking}`,
                    callback: showMoreTrack,
                  }}
                > 
                  {viewMoreCtaText}
                </UitkPrimaryButton>
              </UitkLayoutFlexItem>
            </UitkLayoutFlex>
          </div>
          </ViewMedium>
          </Viewport>
        )}
        {isLoadingNextPage && <DealsLoader />}
      </>
    );
  })
);

const DealsView: React.FC<DealsViewsProps> = inject('context')(
  observer((props) => {
    const { campaign  , isPagination} = props;
    const loadCampaign = campaign;

    const dealOnClickEvent = (module) => {
      gaLogger.logGa('Hotel widget', 'Hotel card click', module);
    };

    const renderDealsCampaignCard = (offer: any, index: number) => {

      let dealUrl = offer?.lodgingOffer?.actionLink?.href;
      if(checkValue(dealUrl)){
        if (dealUrl?.indexOf('mcicid') > -1) {
          // nothing
        }
        else if (dealUrl?.indexOf('?') > -1) {
          dealUrl = dealUrl + '&mcicid=' + tracking; 
        }
        else {
          dealUrl = dealUrl + '?mcicid=' + tracking;
        }
      }
      

      return (
        <UitkCard className="HotelInventoryCard" border={true} data-testid="HotelInventoryCard">
          <UitkFigure ratio={UitkFigureAspectRatioType.R21_9}>
            <UitkSpacing margin={{ blockstart: 'three' }}>
              <UitkLayoutPosition type="absolute" position={{ left: 'three', zindex: 'layer2' }} cloneElement>
                <UitkLayoutFlex space="two">
                  <ImageBadges offer={offer} />
                </UitkLayoutFlex>
              </UitkLayoutPosition>
            </UitkSpacing>
            <LazyLoadedImage src={offer?.image?.url} alt={offer?.image?.description} />
          </UitkFigure>
          <UitkCardContentSection className="HotelInventoryCardSection">
            <UitkSpacing padding={{ blockend: 'four' }}>
              <UitkLayoutPosition type="relative">
                <div>
                  <UitkSpacing padding={{ blockend: 'half' }}>
                    <UitkHeading overflow="truncate" tag="h3" size={5}>
                      {offer?.name}
                    </UitkHeading>
                  </UitkSpacing>
                  <UitkRating rating={`${offer?.starRating}`} />
                  <UitkText size={300}>{offer?.location}</UitkText>
                  {offer?.guestReviewRating && (
                    <UitkLayoutPosition type="absolute" position={{ bottom: 'minus-two' }} cloneElement>
                      <div className="reviewRating">
                        <UitkLayoutFlex>
                          <UitkSpacing padding={{ inlineend: 'one' }}>
                            <UitkText weight="bold" size={300}>
                              {offer?.guestReviewRating}
                            </UitkText>
                          </UitkSpacing>
                          <UitkText size={300} className="pwa-theme--grey-700">
                            {offer?.guestReviewCount}
                          </UitkText>
                        </UitkLayoutFlex>
                      </div>
                    </UitkLayoutPosition>
                  )}
                </div>
              </UitkLayoutPosition>
            </UitkSpacing>
          </UitkCardContentSection>
          <UitkLayoutPosition type="relative">
            <UitkCardContentSection className="HotelInventoryCardSection">
              <Separator offer={offer} />
              {offer?.freeCancellationMessage && (
                <UitkLayoutPosition type="absolute" position={{ top: 'six' }} cloneElement className='hotel-card-offer-summery'>
                  <UitkText size={300} weight="bold" theme="positive">
                    {offer?.freeCancellationMessage}
                  </UitkText>
                </UitkLayoutPosition>
              )}
              <UitkLayoutGrid alignItems="end" columns={1}>
                <>
                  <div className="uitk-type-right uitk-price-container">
                    <PriceComponent
                      offer={offer}
                      cardIndex={index}
                      campaignId={tracking}
                      moduleName={`${tracking}.hotel`}
                      cardClicked={() => {
                        dealOnClickEvent(`${tracking}.hotel.${offer?.id}`);
                      }}
                    />
                  </div>
                </>
              </UitkLayoutGrid>
              <UitkLayoutGrid className="hotel-card-date-container" alignItems="end" columns={1}>
                <>
                  <UitkText align="left" size={200}>
                    {offer?.dates}
                  </UitkText>
                </>
              </UitkLayoutGrid>
            </UitkCardContentSection>
          </UitkLayoutPosition>
          <UitkCardLink>
            {offer?.lodgingOffer && (
              <TrackedLink
                moduleName={'deal'}
                action={Action.CLICK}
                href={dealUrl}
                rfrr={`card-${dealCampaignId}-${index}`}
                mcicid={`mcicid=${tracking}`}
                target={'_blank'}
              >
                {offer?.lodgingOffer?.actionLink?.text}
              </TrackedLink>
            )}
          </UitkCardLink>
        </UitkCard>
      );
    };

    const dealsCarouselViewsProps: DealsCarouselViewsProps = {
      campaign: loadCampaign,
      renderDealsCampaignCard: renderDealsCampaignCard,
      focusElement: null,
      isPagination:isPagination
    };

    return (
      <>
        {checkValue(loadCampaign, true) ? (
          <>
            <UitkSpacing margin={{ blockstart: 'three', inlinestart: 'three', inlineend: 'three' }}>
              {gridSlickView(dealsCarouselViewsProps, loadCampaign, renderDealsCampaignCard)}
            </UitkSpacing>
          </>
        ) : (
          <></>
        )}
      </>
    );
  })
);

// MOBILE/DESKTOP MEDIA STUDIO SLICK VIEW
const mediaStudioSlickView = () => {
  return (<Viewport>
    <ViewSmall>Media Studio Slick</ViewSmall>
  </Viewport>);
};

const PriceComponent = observer(({ offer, cardIndex, campaignId, moduleName, cardClicked }: any) => {
  const offering = offer;
  const { formatText } = useLocalization();
  const [isDialogOpen, dialogActions, PriceDialogComponent] = useDialog(`disclaimer-info-${campaignId}-${cardIndex}`);
  const triggerRef = useRef();

  const handleBodyClick = (e) => {
    document.body.removeEventListener('click', handleBodyClick, true);
    if (e.target.classList.contains('layer-overlay-active')) {
      domManipulationWhenClose();
    }
  };

  const openDialog = () => {
    document.body.addEventListener('click', handleBodyClick, true);
    domManipulationWhenOpen();
    setTimeout(() => {
      dialogActions.openDialog();
    }, 200);
  };

  const closeDialog = () => {
    document.body.removeEventListener('click', handleBodyClick, true);
    dialogActions.closeDialog();
    domManipulationWhenClose();
  };

  const domManipulationWhenClose = () => {
    setTimeout(() => {
      const scrollY = document.body.style.top;
      document.body.style.position = '';
      document.body.style.top = '';
      document.body.style.width = '';
      window.scrollTo(0, parseInt(scrollY || '0') * -1);
    }, 300);
  };

  const domManipulationWhenOpen = () => {
    document.body.style.top = `-${window.scrollY}px`;
    document.body.style.position = 'fixed';
    document.body.style.width = '100%';
  };

  if (offering?.mod?.modSignInText) {
    return (
      <TrackedLink
        moduleName={moduleName}
        action={Action.CLICK}
        rfrr={`football-card-${campaignId}-${cardIndex}`}
        onClick={cardClicked}
        href={offering?.mod?.signInUrl?.relativePath}
        target={'_blank'}
      >
        <UitkText size={300} weight="bold" align="right">
          {offering?.mod?.modSignInText}
        </UitkText>
      </TrackedLink>
    );
  }
  return (
    <>
      <UitkPriceLockup
        lockupPrice={offering?.lodgingOffer?.price?.value}
        priceSubtextFirst={offering?.lodgingOffer?.price?.qualifierText}
        a11yPrice={offering?.lodgingOffer?.price?.allyText || ''} 
        a11yStrikeThroughDialogTrigger={'priceDisclaimerLabel'}
        strikeThroughPrice={offering?.lodgingOffer?.strikeThroughPrice?.value}
        {...(checkValue(offering?.lodgingOffer?.price.disclaimerText) ? {
          strikeThroughTrigger:
            <button className="disclaimerInfoIcon" onClick={openDialog} type="button" ref={triggerRef}>
              <UitkIcon
                name="info_outline"
                size={UitkIconSize.SMALL}
                id={`disclaimer-info-${campaignId}-${cardIndex}`}
                title={formatText('merchCampaign.disclaimerIcon.title')}
              />
            </button>
        } : {})}
      />
      <PriceDialogComponent>
        <InfoDialog
          infoText={offering?.lodgingOffer?.price.disclaimerText}
          closeDialog={closeDialog}
          buttonText={'Close'}  
          isDialogOpen={isDialogOpen}
          triggerRef={triggerRef}
        />
      </PriceDialogComponent>
    </>
  );
});
export { HotelByCampaignId };
